import React, { useCallback, useState } from 'react';
import { useObservable, useRetryableObservable } from 'react-use-observable';

import { App, Button, Modal, Spin, Typography, Upload } from 'antd';
import type { UploadProps } from 'antd';

import chargebackService from 'v2/services/chargeback';

import { Icon } from '@eduzz/ui-antd-theme';

import ChargebackDetailsListItem from './ListItem';

interface IProps {
  visible: boolean;
  invoiceId: number;
  retryLoadChargeback: () => void;
  setModalVisible: (visible: boolean, invoiceId: number | undefined) => void;
}

const ChargebackDetails = ({ setModalVisible, visible, invoiceId, retryLoadChargeback }: IProps) => {
  const { message } = App.useApp();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [hasUploaded, setHasUploded] = useState(false);
  const [chargebackFiles] = useObservable(() => chargebackService.getFiles(), []);

  const [isLoading] = useObservable(() => chargebackService.isLoadingFiles(), []);

  const [, , , retry] = useRetryableObservable(() => chargebackService.loadChargebacksFiles(invoiceId), [invoiceId]);

  const setUpload = useCallback(() => setHasUploded(true), []);

  const handleClose = useCallback(() => {
    if (hasUploaded) {
      retryLoadChargeback();
    }

    setModalVisible(false, undefined);
  }, [hasUploaded, retryLoadChargeback, setModalVisible]);

  const onDrop = async (filesReceived: File[]) => {
    setLoadingSubmit(true);

    try {
      await Promise.all(
        filesReceived.map(async (file: File) => {
          await chargebackService.uploadChargebackFile(file, invoiceId);
        })
      );
      message.success('Arquivo(s) adicionado(s) com sucesso!');
      setUpload();
      setLoadingSubmit(false);
      retry();
    } catch (error) {
      setLoadingSubmit(false);
      message.error(typeof error === 'string' ? error : 'Falha ao enviar arquivo(s)');
    }
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file, fileList) => {
    if (file.uid === fileList[fileList.length - 1].uid) {
      void onDrop(fileList);
    }

    return false;
  };

  if (!visible && invoiceId === undefined) {
    return null;
  }

  return (
    <Modal
      open={visible}
      title='Provas de acesso ao conteúdo'
      onCancel={handleClose}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='primary' onClick={handleClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {loadingSubmit ? (
        <div className='flex flex-col items-center gap-2 py-4'>
          <Spin />
          <Typography.Text>Por favor, aguarde. Estamos fazendo upload do(s) arquivo(s)...</Typography.Text>
        </div>
      ) : (
        <Upload.Dragger multiple showUploadList={false} beforeUpload={beforeUpload} className='mb-4'>
          <div className='flex items-center gap-4 p-2 text-left'>
            <Icon.CloudUpload size={30} className='text-(--uizz-antd-color-text-secondary)' />
            <div>
              <Typography.Paragraph className='mb-1'>Clique ou arraste os arquivos nessa área.</Typography.Paragraph>
              <Typography.Paragraph type='secondary' className='mb-0'>
                Pode ser selecionado vários arquivos ao mesmo tempo!
              </Typography.Paragraph>
            </div>
          </div>
        </Upload.Dragger>
      )}
      {isLoading ? (
        <div className='flex items-center justify-center p-8'>
          <Spin size='large' tip='Carregando arquivo(s) ...' />
        </div>
      ) : (
        <div className='max-h-87.5 overflow-y-auto'>
          {chargebackFiles?.map(item => (
            <ChargebackDetailsListItem key={item.id} setUpload={setUpload} invoiceId={invoiceId} data={item} />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ChargebackDetails;
